import { useEffect } from 'react';
import { useReactFlow, useStore } from '@xyflow/react';

export function CanvasSelectionVisibility({ inspectorOpen, selectedId }: {
  inspectorOpen: boolean;
  selectedId: string | null;
}) {
  const width = useStore(state => state.width);
  const height = useStore(state => state.height);
  const { getNodes, getViewport, setViewport, viewportInitialized } = useReactFlow();

  useEffect(() => {
    if (!inspectorOpen || !width || !height || !viewportInitialized) return;
    const frame = requestAnimationFrame(() => {
      const nodes = getNodes().filter(node => !node.hidden);
      if (!nodes.length) return;
      const viewport = getViewport();
      const left = Math.min(...nodes.map(node => node.position.x));
      const top = Math.min(...nodes.map(node => node.position.y));
      const right = Math.max(...nodes.map(node =>
        node.position.x + (node.measured?.width ?? node.width ?? 280)));
      const bottom = Math.max(...nodes.map(node =>
        node.position.y + (node.measured?.height ?? node.height ?? 140)));

      // Keep the full-page toolbar fixed, but fit the entire graph into the
      // unobstructed area below it and to the left of the inspector.
      const visibleLeft = 24;
      const visibleRight = width - Math.min(380, width * 0.45) - 24;
      const visibleTop = Math.min(96, height * 0.2);
      const visibleBottom = height - 24;
      if (left * viewport.zoom + viewport.x >= visibleLeft &&
          right * viewport.zoom + viewport.x <= visibleRight &&
          top * viewport.zoom + viewport.y >= visibleTop &&
          bottom * viewport.zoom + viewport.y <= visibleBottom) return;

      const zoom = Math.max(0.01, Math.min(
        viewport.zoom,
        (visibleRight - visibleLeft) / Math.max(1, right - left),
        (visibleBottom - visibleTop) / Math.max(1, bottom - top),
      ));
      // Preserve a view that already fits on either axis; otherwise move just
      // enough to bring that whole axis into view at the new zoom.
      const x = Math.max(visibleLeft - left * zoom,
        Math.min(viewport.x, visibleRight - right * zoom));
      const y = Math.max(visibleTop - top * zoom,
        Math.min(viewport.y, visibleBottom - bottom * zoom));
      void setViewport({ x, y, zoom }, { duration: 200 });
    });
    return () => cancelAnimationFrame(frame);
  }, [inspectorOpen, selectedId, width, height, viewportInitialized, getNodes, getViewport, setViewport]);

  return null;
}
